import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import SignalCard from "../components/SignalCard";
import OpportunityCard from "../components/OpportunityCard";
import PredictionLoop from "../components/PredictionLoop";
import { Activity, TrendingUp, Target, Rocket, Globe } from "lucide-react";

function StatTile({ label, value, sub, accent = "text-bm-cyan", testId }) {
  return (
    <div className="bm-card p-4" data-testid={testId}>
      <div className="label-mono text-bm-dim mb-2">{label}</div>
      <div className={`font-heading font-black text-3xl ${accent}`}>{value}</div>
      {sub && <div className="label-mono text-bm-muted mt-1">{sub}</div>}
    </div>
  );
}

export default function HomeFeed() {
  const [stats, setStats] = useState(null);
  const [signals, setSignals] = useState([]);
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.stats().then(setStats);
    api.signals({ limit: 9 }).then((d) => setSignals(d.items || []));
    api.opportunities({ min_conviction: 80 }).then((d) => setOpps((d.items || []).slice(0, 3)));
    // Fire-once on mount — api / setters are stable references; intentional empty deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 border-b border-white/10 pb-4">
        <div>
          <div className="label-mono text-bm-amber">LIVE INTELLIGENCE FEED</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight mt-1 text-white" data-testid="page-title">
            What is changing.
          </h1>
          <p className="text-sm text-bm-muted mt-2 max-w-2xl">
            Continuous ingestion across 14 commerce + social platforms in 6 regions. Validated through 5-layer decision trees. Outputs ranked by India launch conviction.
          </p>
        </div>
        <div className="font-mono text-xs text-bm-dim text-right">
          <div>{new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</div>
          <div className="text-bm-green flex items-center gap-1 justify-end mt-1">
            <span className="w-1.5 h-1.5 bg-bm-green rounded-full animate-pulse"></span>SYSTEM LIVE
          </div>
        </div>
      </div>

      {/* Prediction loop */}
      <PredictionLoop />

      {/* Stat tiles */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stat-grid">
          <StatTile testId="stat-signals" label="SIGNALS TRACKED" value={stats.signals_tracked} accent="text-bm-cyan" />
          <StatTile testId="stat-opps" label="OPPORTUNITIES" value={stats.opportunities_active} accent="text-bm-amber" />
          <StatTile testId="stat-high-conv" label="HIGH CONVICTION" value={stats.high_conviction_opportunities} sub="≥80% overall" accent="text-bm-green" />
          <StatTile testId="stat-launches" label="LIVE LAUNCHES" value={stats.live_launches} accent="text-white" />
          <StatTile testId="stat-velocity" label="AVG VELOCITY" value={`${stats.avg_signal_velocity}`} sub="across signals" accent="text-bm-cyan" />
          <StatTile testId="stat-regions" label="REGIONS / PLATFORMS" value={`${stats.regions_tracked}/${stats.platforms_monitored}`} accent="text-bm-amber" />
        </div>
      )}

      {/* Two-column: feed + top opps */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-3">
          <div className="flex items-center justify-between">
            <div className="label-mono text-bm-cyan flex items-center gap-2"><Activity className="w-3 h-3" />SIGNAL TICKER</div>
            <div className="label-mono text-bm-dim">SORTED BY VELOCITY</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="signals-grid">
            {signals.map((s, i) => <SignalCard key={s.id} signal={s} idx={i} />)}
          </div>
        </section>

        <aside className="space-y-3">
          <div className="label-mono text-bm-amber flex items-center gap-2"><Target className="w-3 h-3" />TOP CONVICTION OPPORTUNITIES</div>
          <div className="space-y-3" data-testid="top-opps">
            {opps.map((o, i) => <OpportunityCard key={o.id} opp={o} idx={i} />)}
          </div>
        </aside>
      </div>
    </div>
  );
}
